import { useDl } from "../utils/dlToken";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, FileText, Video, Trash2, Eye, Loader2, FileUp, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 4 * 1024 * 1024; // 4 MB
const MAX_DOC = 15 * 1024 * 1024;
const MAX_VIDEO = 100 * 1024 * 1024;

export default function PortalUploads({ token, t, application, reload }) {
  const dl = useDl(token);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const submitted = !!application?.documents_submitted;

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/applicant/files`, auth); setFiles(data); }
    catch { /* ignore */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file, kind) => {
    setError("");
    if (!file) return;
    const max = kind === "video" ? MAX_VIDEO : MAX_DOC;
    if (file.size > max) {
      setError(`${t.upTooBig} (max ${kind === "video" ? "100 MB" : "15 MB"}).`);
      return;
    }
    setBusy(kind); setProgress(0);
    try {
      const { data: begin } = await axios.post(`${API}/applicant/upload/begin`,
        { filename: file.name, content_type: file.type, kind }, auth);
      const uploadId = begin.upload_id;
      let sent = 0;
      for (let start = 0; start < file.size; start += CHUNK) {
        const blob = file.slice(start, start + CHUNK);
        await axios.put(`${API}/applicant/upload/chunk/${uploadId}`, blob, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/octet-stream" },
        });
        sent += blob.size;
        setProgress(Math.round((sent / file.size) * 100));
      }
      await axios.post(`${API}/applicant/upload/finish/${uploadId}`, {}, auth);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t.upFailed);
    } finally { setBusy(""); setProgress(0); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try { await axios.delete(`${API}/applicant/files/${id}`, auth); setFiles((f) => f.filter((x) => x.id !== id)); }
    catch { /* ignore */ }
  };

  const view = (id) => window.open(`${API}/files/${id}/download?token=${dl}`, "_blank");

  const submitDocs = async () => {
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/applicant/submit-documents`, {}, auth);
      reload && reload();
    } catch (err) {
      setError(err.response?.data?.detail || t.upSubmitErr);
    } finally { setSubmitting(false); }
  };

  const docs = files.filter((f) => f.kind === "document");
  const video = files.find((f) => f.kind === "video");

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-6 mt-8" data-testid="portal-uploads">
      <h3 className="font-display text-xl mb-1">{t.upTitle}</h3>
      <p className="text-white/50 text-sm mb-6">{t.upDesc}</p>

      {error && <p data-testid="upload-error" className="text-red-400 text-sm mb-4">{error}</p>}

      <div className="grid md:grid-cols-2 gap-4">
        <label data-testid="upload-document-label"
          className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 hover:border-[#D4AF37] py-7 cursor-pointer transition-colors text-center">
          {busy === "document" ? <Loader2 className="animate-spin text-[#D4AF37]" size={22} /> : <FileUp className="text-[#D4AF37]" size={22} />}
          <span className="text-sm text-white/70">{busy === "document" ? `${t.upUploading} ${progress}%` : t.upDoc}</span>
          <span className="text-xs text-white/35">{t.upDocHint}</span>
          <input type="file" data-testid="upload-document-input" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.heic" className="hidden"
            disabled={!!busy} onChange={(e) => upload(e.target.files[0], "document")} />
        </label>

        <label data-testid="upload-video-label"
          className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 hover:border-[#D4AF37] py-7 cursor-pointer transition-colors text-center">
          {busy === "video" ? <Loader2 className="animate-spin text-[#D4AF37]" size={22} /> : <Video className="text-[#D4AF37]" size={22} />}
          <span className="text-sm text-white/70">{busy === "video" ? `${t.upUploading} ${progress}%` : (video ? t.upVideoReplace : t.upVideo)}</span>
          <span className="text-xs text-white/35">{t.upVideoHint}</span>
          <input type="file" data-testid="upload-video-input" accept="video/*,.mp4,.mov,.webm,.m4v" className="hidden"
            disabled={!!busy} onChange={(e) => upload(e.target.files[0], "video")} />
        </label>
      </div>

      {files.length > 0 && (
        <ul className="mt-6 space-y-2" data-testid="uploaded-files">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 text-sm">
              <span className="flex items-center gap-2.5 min-w-0">
                {f.kind === "video" ? <Video size={16} className="text-[#D4AF37] shrink-0" /> : <FileText size={16} className="text-[#D4AF37] shrink-0" />}
                <span className="truncate text-white/80">{f.original_filename}</span>
                <span className="text-white/30 text-xs shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <button onClick={() => view(f.id)} data-testid="file-view" className="text-white/50 hover:text-[#D4AF37]" title="Zobraziť"><Eye size={16} /></button>
                <button onClick={() => remove(f.id)} data-testid="file-delete" className="text-white/50 hover:text-red-400" title="Zmazať"><Trash2 size={16} /></button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {submitted ? (
        <div className="mt-6 flex items-center gap-3 border border-green-500/30 bg-green-500/5 p-4" data-testid="docs-submitted-note">
          <CheckCircle2 size={18} className="text-green-400 shrink-0" />
          <p className="text-white/80 text-sm">{t.upSubmitDone}</p>
        </div>
      ) : files.length > 0 && (
        <div className="mt-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5" data-testid="docs-submit-card">
          <p className="font-display text-lg">{t.upSubmitTitle}</p>
          <p className="text-white/55 text-sm mt-1 mb-4">{t.upSubmitHint}</p>
          <button onClick={submitDocs} disabled={submitting} data-testid="submit-documents-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {submitting ? t.upSubmitting : t.upSubmitBtn}
          </button>
        </div>
      )}
    </div>
  );
}
